import { EnumVariant, Tag, Tags } from '../interfaces';

/** Filter key carrying a tag definition's stable identifier, e.g. 'tdi:graffiti'. */
export const TAG_IDENTIFIER_KEY_PREFIX = 'tdi:';

/** Filter key carrying a tag definition's environment-specific numeric ID, e.g. 'td:776'. */
export const TAG_ID_KEY_PREFIX = 'td:';

export const tagIdentifierFilterKey = (identifier: string): string => `${TAG_IDENTIFIER_KEY_PREFIX}${identifier}`;

/**
 * The key a newly selected tag should be stored under: the identifier form when the definition
 * has an identifier, so the resulting filter is portable across environments.
 */
export const tagFilterKey = (tag: Tag): string =>
  tag.identifier ? tagIdentifierFilterKey(tag.identifier) : `${TAG_ID_KEY_PREFIX}${tag.id}`;

/**
 * The key a tag is currently selected under, or undefined if it is not selected.
 *
 * Both key forms have to be recognised: filters are persisted into a shareable URL, so a link
 * created before the switch to identifiers still carries numeric keys and must keep working.
 */
export const selectedTagFilterKey = (tag: Tag, selectedTags: Tags): string | undefined =>
  [tagFilterKey(tag), `${TAG_ID_KEY_PREFIX}${tag.id}`].find((key) => key in selectedTags);

/**
 * Serializes an enum variant identifier as a filter value, e.g. 'evi:graffiti'. Variant
 * identifiers are only unique within their parent definition, so this is meaningful only
 * alongside the key naming that definition.
 */
export const tagVariantIdentifierFilterValue = (identifier: string): string => `evi:${identifier}`;

/**
 * The value a selected tag should be stored under, mirroring the key side so that neither half of
 * a persisted filter carries an environment-specific ID. Falls back to the numeric form for a
 * variant without an identifier, and to an empty value for a tag with no variants at all.
 */
export const tagFilterValue = (variant?: EnumVariant): string => {
  if (!variant) {
    return '';
  }

  return variant.identifier ? tagVariantIdentifierFilterValue(variant.identifier) : `ev:${variant.id}`;
};

/**
 * Builds the tag query predicate for one filter entry. An identifier key is sent as
 * tag_identifier for the backend to resolve; a numeric key is sent as tag_definition_id.
 */
export const buildTagPredicate = (key: string, value: Tags[0]) => {
  if (key.startsWith(TAG_IDENTIFIER_KEY_PREFIX)) {
    return { tag_identifier: key.slice(TAG_IDENTIFIER_KEY_PREFIX.length), op: '=' as const, value };
  }

  return { tag_definition_id: parseInt(key.split(':')[1], 10), op: '=' as const, value };
};
